import equal from "fast-deep-equal"
import React, { RefObject, useEffect, useRef, useState } from "react"
import { useAtomValue } from "jotai"
import { Icon } from "../icons/Icon"
import { CurveAdjustmentPoint, CurveEditor } from "./CurveEditor"
import { useEditor } from "@/utils/useEditor"
import { ColorSwatches, SwatchesList } from "../Swatches"
import { extractParametricAdjustments, ParametricCurves } from "@/components/ParametricCurves"
import { clsx } from "clsx"
import { vec2 } from "gl-matrix"
import { Adjustments } from "@/core/types"
import { getDefaultAdjustments } from "@/core/adjustments"
import { themeModeAtom } from "@/store/settings-state"
import { getSystemThemeMediaQuery, resolveThemeMode } from "@/utils/theme-mode"

const CurvesColors: ColorSwatches[] = ["white", "red", "green", "blue"]

type CurveKey = keyof Pick<Adjustments, "curvesAll" | "curvesRed" | "curvesBlue" | "curvesGreen">

interface CurveAdjustmentDefinition {
	name: CurveKey
	color: string
}

const CURVE_SWATCH_MAPPING = {
	curvesAll: "white",
	curvesRed: "red",
	curvesGreen: "green",
	curvesBlue: "blue",
} as const satisfies Record<CurveKey, ColorSwatches>

const SWATCH_CURVE_MAPPING = Object.fromEntries(
	Object.entries(CURVE_SWATCH_MAPPING).map(([curve, swatch]) => [swatch, curve]),
) as Record<ColorSwatches, CurveKey>

function mapSwatchCurve(swatch: ColorSwatches): CurveKey {
	return SWATCH_CURVE_MAPPING[swatch] ?? "curvesAll"
}

function mapCurveSwatch(curve: CurveKey): ColorSwatches {
	return CURVE_SWATCH_MAPPING[curve] ?? "white"
}

function getNormalizedCurve(state: vec2[]): CurveAdjustmentPoint[] {
	return state.map((point) => {
		return {
			x: point[0] / 255.0,
			y: point[1] / 255.0,
		}
	})
}

export const Curves = (props: {
	values: Adjustments
	saveChange: (name: string, val?: string | number) => void
}) => {
	/* Local state */

	const editor = useEditor()

	const [selectedKnobPosition, setSelectedKnobPosition] = useState<CurveAdjustmentPoint | null>(
		null,
	)
	const [selectedAdjustmentName, setSelectedAdjustmentName] = useState<CurveKey>("curvesAll")

	// TODO: temporary
	const [isParametricSelected, setIsParametricSelected] = useState(true)
	const themeMode = useAtomValue(themeModeAtom)
	const [isSystemDark, setIsSystemDark] = useState<boolean>(() => {
		return getSystemThemeMediaQuery()?.matches ?? false
	})

	useEffect(() => {
		if (themeMode !== "system") {
			return
		}

		const mediaQuery = getSystemThemeMediaQuery()
		if (!mediaQuery) {
			return
		}

		// Sync immediately when entering system mode to avoid stale theme state.
		setIsSystemDark(mediaQuery.matches)

		const handleChange = (event: MediaQueryListEvent) => {
			setIsSystemDark(event.matches)
		}

		if (typeof mediaQuery.addEventListener === "function") {
			mediaQuery.addEventListener("change", handleChange)
			return () => mediaQuery.removeEventListener("change", handleChange)
		}

		mediaQuery.addListener(handleChange)
		return () => mediaQuery.removeListener(handleChange)
	}, [themeMode])

	const resolvedTheme = resolveThemeMode(themeMode, isSystemDark)

	const selectedAdjustmentColor: ColorSwatches = mapCurveSwatch(selectedAdjustmentName)

	const allAdjustments: CurveAdjustmentDefinition[] = [
		{ name: "curvesAll", color: resolvedTheme === "dark" ? "#f5f5f5" : "#202020" },
		{ name: "curvesRed", color: "#E40067" },
		{ name: "curvesGreen", color: "#00E47D" },
		{ name: "curvesBlue", color: "#0073FF" },
	]
	const gridColor = resolvedTheme === "dark" ? "rgba(255, 255, 255, 0.14)" : "rgba(0, 0, 0, 0.26)"

	const allRefs: Record<CurveKey, RefObject<(CurveEditor & HTMLCanvasElement) | null>> = {
		curvesAll: useRef<(CurveEditor & HTMLCanvasElement) | null>(null),
		curvesRed: useRef<(CurveEditor & HTMLCanvasElement) | null>(null),
		curvesGreen: useRef<(CurveEditor & HTMLCanvasElement) | null>(null),
		curvesBlue: useRef<(CurveEditor & HTMLCanvasElement) | null>(null),
	}

	const containerRef = useRef<HTMLDivElement>(null)

	/* Effects */

	useEffect(() => {
		allAdjustments.forEach((adjustment) => {
			allRefs[adjustment.name].current?.update(
				getNormalizedCurve(props.values[adjustment.name]),
			)
		})
	}, [props.values])

	/* Actions */

	const onUpdate = (name: string, points: CurveAdjustmentPoint[]) => {
		const renormalizedPoints = points.map((point) => {
			return [point.x * 255, point.y * 255]
		})

		editor.setAdjustments({ [name]: renormalizedPoints })
	}

	const onStartUpdating = (_: string) => {}

	const onEndUpdating = (name: string) => {
		let message: string

		switch (name) {
			case "curvesAll":
				message = "Curves: Custom"
				break
			case "curvesRed":
				message = "Curves: Red Channel"
				break
			case "curvesGreen":
				message = "Curves: Green Channel"
				break
			case "curvesBlue":
				message = "Curves: Blue Channel"
				break
			default:
				message = "Curves"
		}

		props.saveChange(message)
	}

	const onKnobPositionUpdate = (position: CurveAdjustmentPoint | null) => {
		if (position === null) {
			setSelectedKnobPosition(null)
			return
		}

		const x = Math.round(position.x * 255)
		const y = Math.round(position.y * 255)

		setSelectedKnobPosition({
			x,
			y,
		})
	}

	const checkIfSelectedAdjustmentChanged = (): boolean => {
		const defaultAdjustments = getDefaultAdjustments()
		if (isParametricSelected) {
			const defaultState = extractParametricAdjustments(defaultAdjustments)
			const currentState = extractParametricAdjustments(props.values)
			return equal(defaultState, currentState)
		}

		const defaultState = defaultAdjustments[selectedAdjustmentName]
		const currentState = editor.adjustments[selectedAdjustmentName]
		return equal(defaultState, currentState)
	}

	const resetSelectedCurve = () => {
		const defaultAdjustments = getDefaultAdjustments()
		if (isParametricSelected) {
			const defaultState = extractParametricAdjustments(defaultAdjustments)

			editor.setAdjustments(defaultState)
			props.saveChange(`Reset parametric curve`)

			return
		}

		const defaultState = defaultAdjustments[selectedAdjustmentName]

		onStartUpdating(selectedAdjustmentName)
		onEndUpdating(selectedAdjustmentName)

		setSelectedKnobPosition(null)

		// Directly resetting the underlying component because it has its own state.
		allRefs[selectedAdjustmentName].current?.reset()

		editor.setAdjustments({
			[selectedAdjustmentName]: defaultState,
		})
		props.saveChange(`Reset ${selectedAdjustmentName} curve`)
	}

	const updateSelectedAdjustmentName = (_: React.MouseEvent, name: ColorSwatches) => {
		const curveKey = mapSwatchCurve(name)

		setIsParametricSelected(false)
		setSelectedAdjustmentName(curveKey)
		allRefs[selectedAdjustmentName].current?.deselect()
		setSelectedKnobPosition(null)
	}

	const selectParametricCurves = () => {
		allRefs[selectedAdjustmentName].current?.deselect()
		setIsParametricSelected(true)
	}

	/* Rendering */

	const renderActions = () => {
		const isDisabled = checkIfSelectedAdjustmentChanged()

		return (
			<Icon
				className={`ml-auto ${isDisabled === true ? "opacity-50" : ""}`}
				disabled={isDisabled}
				onClick={resetSelectedCurve}
				name="reset"
			/>
		)
	}

	const renderInfo = () => {
		return (
			<span
				className={`absolute left-0 top-0 text-xs text-base-400 ${
					selectedKnobPosition === null ? "opacity-0" : "opacity-100"
				}`}
			>
				{selectedKnobPosition?.x || 0} / {selectedKnobPosition?.y || 0}
			</span>
		)
	}

	const renderAdjustments = () => {
		return (
			<>
				{allAdjustments.map((adjustment) => {
					const isSelected = adjustment.name === selectedAdjustmentName

					return (
						<CurveEditor
							ref={allRefs[adjustment.name]}
							key={adjustment.name}
							name={adjustment.name}
							color={adjustment.color}
							points={getNormalizedCurve(props.values[adjustment.name])}
							onUpdate={onUpdate}
							onStartUpdating={onStartUpdating}
							onEndUpdating={onEndUpdating}
							onKnobPositionUpdate={onKnobPositionUpdate}
							gridColor={gridColor}
							disabled={isSelected === false}
							className={`absolute left-0 top-0 size-full ${
								isSelected === true ? "" : "pointer-events-none opacity-[10%]"
							}`}
						/>
					)
				})}
			</>
		)
	}

	return (
		<div
			ref={containerRef}
			className="box-border flex w-full shrink-0 select-none flex-col overflow-hidden"
		>
			<div className="flex w-full flex-row items-center gap-3">
				<Icon
					className={clsx(
						"rounded-full",
						isParametricSelected ? "bg-base-200 text-base-800" : "bg-base-700",
					)}
					name="parametric_curves"
					onClick={selectParametricCurves}
					size={16}
				/>
				<SwatchesList
					compact={true}
					colors={CurvesColors}
					selected={!isParametricSelected ? selectedAdjustmentColor : null}
					onChange={updateSelectedAdjustmentName}
				/>

				{renderActions()}
			</div>
			{isParametricSelected ? (
				<ParametricCurves {...props} isLightMode={resolvedTheme === "light"} />
			) : (
				<div className="relative mt-2 aspect-square w-full">
					{renderAdjustments()}
					{renderInfo()}
				</div>
			)}
		</div>
	)
}
